import {Trans} from '@lingui/macro';
import React, {Component, ReactNode} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {emojiToUnified} from '../../../../../srcCommon/helpers/emoji/emojiHelpers';
import {EmojiDefinitionAndSkinTone} from '../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {computeHighlightPickerValues} from '../../../../helpers/models/highlightHelpers';
import {isTeamNamespace} from '../../../../helpers/models/namespaceHelpers';
import {HighlightTypesEnum} from '../../../../models/api/highlightBaseModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {SearchTagModel} from '../../../../models/api/searchTagModel';
import {TagModel, TagTypesEnum} from '../../../../models/api/tagModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {PaletteColorPicker} from '../../color/paletteColorPicker';
import {DialogButtonGroup} from '../../dialogs/dialogButtonGroup';
import {DialogContent} from '../../dialogs/dialogContent';
import {EmojiSelectHandler} from '../emoji/emojiDropdown';
import {NamespaceDropdownBoxContainer} from '../namespace/namespaceDropdownBoxContainer';
import {PickParentTagDropdownBox} from './pickParentTagDropdownBox';
import {PickTeammateWorkspacesDropdownBox} from './pickTeammateWorkspacesDropdownBox';
import {TagEditorCreationError} from './tagEditorCreationError';
import {TagEditorEmoji} from './tagEditorEmoji';
import {ShowTagInCellsTooltip, ShowTagInWorkspacesTooltip} from './tagEditorTooltips';

/*
 * Props.
 */

export interface TagTemplate {
  name: string;
  namespace: string;
  parentTags: ReadonlyArray<TagModel>;
  highlightType: HighlightTypesEnum;
  type?: TagTypesEnum;
  highlightEmoji?: string;
  highlightColor?: PaletteColorsEnum;
  showInWorkspaces: boolean;
  showInCells: boolean;
  teammatesToPin: ReadonlyArray<number>;
  isVisibleInAllInboxes?: boolean;
  inboxIds?: ReadonlyArray<number>;
  isSlaAppliesTag?: boolean;
}

export interface TagEditorBodyPublicProps {
  userId: number;
  teammates: ReadonlyArray<TeammateModel>;
  title: ReactNode;
  resourceNamespace?: string;
  okButtonLabel: ReactNode;
  onOkClick: HandlerOf<TagTemplate>;
  isEditing?: boolean;
  isSystemTag?: boolean;
  hasPermission: (namespace: string, permission: PermissionsEnum) => boolean;
  onRequestClose?: Handler;
  shouldHideParentTagSelector?: boolean;
  shouldHideNamespaceSelector?: boolean;
  isSaving: boolean;
  tagError: CustomError | undefined;
  shouldHideShowInWorkspace?: boolean;
}

export interface TagEditorBodyInitialState {
  name: string;
  namespace: string;
  parentTags: ReadonlyArray<TagModel>;
  highlightEmoji?: EmojiDefinitionAndSkinTone;
  highlightColor?: PaletteColorsEnum;
  showInWorkspaces: boolean;
  showInCells: boolean;
  selectedTeammateIds?: ReadonlyArray<number>;
}

export interface TagEditorBodyProps extends TagEditorBodyPublicProps {
  tagId?: number;
  initialState: TagEditorBodyInitialState;
  extraDropdownItems?: ReactNode;
}

/*
 * Styles.
 */

const StyledDialogContentDiv = styled.div`
  flex-grow: 1;

  display: flex;
  flex-direction: column;

  // Allow scrolling if the dialog height is limited.
  overflow: auto;
`;

const StyledDialogMainContentDiv = styled.div`
  flex: 1;
`;

const StyledSectionDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  grid-template-areas: 'title toggle';
  align-items: center;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledFieldDiv = styled.div`
  & + & {
    margin-top: 9px;
  }
`;

const StyledHighlightDiv = styled.div`
  margin: 15px 0 18px;

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  grid-column-gap: 5px;
  align-items: center;
  justify-content: center;
`;

const StyledIconDiv = styled.div`
  display: flex;
  align-items: center;
  padding-left: 6px;
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledLabelIconDiv = styled.div`
  display: inline-block;
  padding-left: 6px;
  margin-bottom: -4px;
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledCheckboxTooltipDiv = styled.div`
  display: flex;
  padding: 11px 0 10px;
  & + & {
    padding-top: 4px;
  }
`;

const StyledWorkspacesDropdownWrapperDiv = styled.div`
  margin-top: 10px;
`;

/*
 * Component.
 */

interface TagEditorBodyState extends TagEditorBodyInitialState {
  isDiscardDialogOpen?: boolean;
}

export class TagEditorBody extends Component<TagEditorBodyProps, TagEditorBodyState> {
  constructor(props: TagEditorBodyProps) {
    super(props);
    this.state = props.initialState;
  }

  /*
   * Event handlers.
   */

  private readonly onNameChange: HandlerOf<string> = (newName) => {
    this.setState({name: newName});
  };

  private readonly onNamespaceSelect: HandlerOf<string> = (newNamespace) => {
    this.setState((prevState) => {
      // It is possible for the user to open the dropdown and select the same namespace.
      const selectedTeammateIds =
        newNamespace === prevState.namespace ? prevState.selectedTeammateIds : [this.props.userId];

      return {
        namespace: newNamespace,
        parentTags: [],
        selectedTeammateIds,
      };
    });
  };

  private readonly onParentTagSelect: HandlerOf<SearchTagModel | undefined> = (newParentTags) => {
    this.setState({parentTags: newParentTags || []});
  };

  private readonly onColorSelect: HandlerOf<PaletteColorsEnum | undefined> = (paletteColor) => {
    this.setState({
      highlightEmoji: undefined,
      highlightColor: paletteColor === PaletteColorsEnum.GREY ? undefined : paletteColor,
    });
  };

  private readonly onEmojiSelect: EmojiSelectHandler = (_shortcode, definition) => {
    this.setState({
      highlightEmoji: definition,
      highlightColor: undefined,
    });
  };

  private readonly onShowInWorkspacesChange: HandlerOf<boolean> = (showInWorkspaces) => {
    this.setState({showInWorkspaces});
  };

  private readonly onShowInCellsChange: HandlerOf<boolean> = (showInCells) => {
    this.setState({showInCells});
  };

  private readonly onOkClickAsync = async () => {
    if (!isValidState(this.state)) {
      return;
    }

    const {onOkClick} = this.props;

    const saveTagOptions = buildTemplate(this.props, this.state);
    onOkClick(saveTagOptions);
  };

  private readonly onSelectedTeammatesChange: HandlerOf<ReadonlyArray<number>> = (newSelectedTeammates) => {
    this.setState({selectedTeammateIds: newSelectedTeammates});
  };

  /*
   * Render.
   */

  render() {
    return <>{this.maybeRenderEditTag()}</>;
  }

  private maybeRenderHeaderRight() {
    const dropdownItems = this.props.extraDropdownItems;
    if (!dropdownItems) {
      return null;
    }

    return (
      <PopoverCoordinator
        renderButton={(isOpen) => (
          <IconButton
            label={<Trans id="tageditorbody-more">More</Trans>}
            iconName="moreVerticalSmall"
            hasHover={isOpen}
            size={VisualSizesEnum.SMALL}
          />
        )}
        renderModal={(modalProps) => (
          <SimpleDropdown {...modalProps} placement="bottom-end" width={112}>
            {dropdownItems}
          </SimpleDropdown>
        )}
      />
    );
  }

  private maybeRenderEditTag() {
    if (this.props.isSaving) {
      return null;
    }

    const keyHandlers = {
      [KeyboardActionsEnum.CONFIRM]: this.onOkClickAsync,
    };

    return (
      <KeyboardShortcuts handlers={keyHandlers}>
        <DropdownHeader title={this.props.title} rightIcon={this.maybeRenderHeaderRight()} />
        <StyledDialogContentDiv>
          <StyledDialogMainContentDiv>
            <DialogContent>
              {this.renderNameInput()}
              {this.maybeRenderNamespaceBox()}
              {this.renderHighlightPicker()}
              {this.maybeRenderPickParentTagDropdownBox()}
              {this.renderShowInWorkspaces()}
              {this.renderShowInConversationList()}
            </DialogContent>
          </StyledDialogMainContentDiv>
          {this.renderButtons()}
        </StyledDialogContentDiv>
      </KeyboardShortcuts>
    );
  }

  private renderNameInput() {
    const {name} = this.state;
    const {tagError} = this.props;
    return (
      <StyledFieldDiv>
        <TextBox
          label={<Trans id="tageditor-name">Name</Trans>}
          value={name}
          onChange={this.onNameChange}
          hasError={tagError !== undefined}
          errorMessage={<TagEditorCreationError error={tagError} />}
          shouldAutoFocus
          isRequired
          isDisabled={this.props.isSystemTag}
        />
      </StyledFieldDiv>
    );
  }

  private maybeRenderPickParentTagDropdownBox() {
    const {parentTags, namespace} = this.state;
    const {shouldHideParentTagSelector} = this.props;

    if (shouldHideParentTagSelector) {
      return null;
    }

    return (
      <StyledFieldDiv>
        <PickParentTagDropdownBox
          namespace={namespace}
          onTagSelect={this.onParentTagSelect}
          selectedTag={parentTags[0]}
          excludeTagId={this.props.tagId}
          isDisabled={this.props.isSystemTag}
        />
      </StyledFieldDiv>
    );
  }

  private maybeRenderNamespaceBox() {
    const {resourceNamespace, isEditing, shouldHideNamespaceSelector} = this.props;

    if (shouldHideNamespaceSelector) {
      return null;
    }

    const {namespace} = this.state;
    return (
      <StyledFieldDiv>
        <NamespaceDropdownBoxContainer
          placeholder={<Trans id="tageditor-access-placeholder">Select a team or make it private</Trans>}
          resourceNamespace={resourceNamespace}
          selectedNamespace={namespace}
          onNamespaceSelect={this.onNamespaceSelect}
          isDisplayOnly={isEditing}
          permission={PermissionsEnum.EDIT_TAG}
          isRequired
        />
      </StyledFieldDiv>
    );
  }

  private renderHighlightPicker() {
    const {highlightColor, highlightEmoji} = this.state;
    const displayValues = computeHighlightPickerValues(highlightColor, highlightEmoji);

    return (
      <StyledFieldDiv>
        <StyledSectionDiv>
          <StyledTitleDiv>
            <Trans id="tageditor-highlight">Color or emoji</Trans>
          </StyledTitleDiv>
        </StyledSectionDiv>
        <StyledHighlightDiv>
          <PaletteColorPicker paletteColor={displayValues.color} onColorSelect={this.onColorSelect} />
          <TagEditorEmoji
            emoji={displayValues.emoji.emoji}
            skinTone={displayValues.emoji.skinTone}
            onEmojiSelect={this.onEmojiSelect}
            isSelected={Boolean(highlightEmoji)}
          />
        </StyledHighlightDiv>
      </StyledFieldDiv>
    );
  }

  private renderShowInWorkspaces() {
    if (this.props.shouldHideShowInWorkspace) {
      return null;
    }

    const shouldUsePinFeature = computeShouldUsePinFeature(this.props, this.state.namespace);

    if (shouldUsePinFeature) {
      const label = (
        <>
          <Trans id="tageditor-showinothersidebars">Show in teammate sidebars</Trans>

          <StyledLabelIconDiv>
            <TooltipCoordinator render={this.renderShowInWorkspacesTooltip} placement="top" isMultiline />
          </StyledLabelIconDiv>
        </>
      );

      return (
        <StyledWorkspacesDropdownWrapperDiv>
          <PickTeammateWorkspacesDropdownBox
            namespace={this.state.namespace}
            teammates={this.props.teammates}
            selectedTeammateIds={this.state.selectedTeammateIds || [this.props.userId]}
            onSelectedTeammatesChange={this.onSelectedTeammatesChange}
            label={label}
          />
        </StyledWorkspacesDropdownWrapperDiv>
      );
    }

    return (
      <StyledCheckboxTooltipDiv>
        <CheckBox isChecked={this.state.showInWorkspaces} onChange={this.onShowInWorkspacesChange}>
          <Trans id="tageditor-showinsidebar">Show in sidebar</Trans>
        </CheckBox>
        <StyledIconDiv>
          <TooltipCoordinator render={this.renderShowInWorkspacesTooltip} placement="top" isMultiline />
        </StyledIconDiv>
      </StyledCheckboxTooltipDiv>
    );
  }

  private renderShowInConversationList() {
    return (
      <StyledCheckboxTooltipDiv>
        <CheckBox isChecked={this.state.showInCells} onChange={this.onShowInCellsChange}>
          <Trans id="tageditor-showincells">Display in conversation lists</Trans>
        </CheckBox>
        <StyledIconDiv>
          <TooltipCoordinator render={this.renderShowInCellsTooltip} placement="top" isMultiline />
        </StyledIconDiv>
      </StyledCheckboxTooltipDiv>
    );
  }

  private readonly renderShowInWorkspacesTooltip = () => <ShowTagInWorkspacesTooltip />;

  private readonly renderShowInCellsTooltip = () => <ShowTagInCellsTooltip />;

  private renderButtons() {
    const canSave = isValidState(this.state);

    return (
      <DialogButtonGroup>
        <ButtonGroupSpacer />
        <Button onClick={this.props.onRequestClose}>
          <Trans id="dropdown-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={this.onOkClickAsync} isDisabled={!canSave}>
          {this.props.okButtonLabel}
        </Button>
      </DialogButtonGroup>
    );
  }
}

/*
 * State helpers.
 */

function isValidState(state: TagEditorBodyState): boolean {
  const {name, namespace} = state;
  return Boolean(name && namespace);
}

function buildTemplate(props: TagEditorBodyPublicProps, state: TagEditorBodyState): TagTemplate {
  const {name, namespace, parentTags, showInCells, selectedTeammateIds = []} = state;

  const shouldUsePinFeature = computeShouldUsePinFeature(props, namespace);

  // If using the new multi-teammate select, the showInWorkspaces checkbox is hidden.
  const showInWorkspaces =
    shouldUsePinFeature && selectedTeammateIds.length
      ? selectedTeammateIds.includes(props.userId)
      : state.showInWorkspaces;

  const baseProps = {
    name,
    namespace,
    parentTags,
    showInWorkspaces,
    showInCells,
    teammatesToPin: shouldUsePinFeature ? selectedTeammateIds : [],
  };

  const {highlightColor, highlightEmoji} = state;
  if (highlightColor) {
    return {
      ...baseProps,
      highlightColor,
      highlightType: HighlightTypesEnum.COLOR,
    };
  }

  if (highlightEmoji) {
    return {
      ...baseProps,
      highlightEmoji: emojiToUnified(highlightEmoji.emoji, highlightEmoji.skinTone),
      highlightType: HighlightTypesEnum.EMOJI,
    };
  }

  return {
    ...baseProps,
    highlightType: HighlightTypesEnum.NONE,
  };
}

/* Check if the UI should display the multi-teammate pin feature, and whether showInWorkspaces should be disabled. */
function computeShouldUsePinFeature(props: TagEditorBodyPublicProps, namespace: string) {
  if (props.isEditing) {
    return false;
  }

  if (!isTeamNamespace(namespace)) {
    return false;
  }

  return props.hasPermission(namespace, PermissionsEnum.EDIT_TEAMMATE);
}
